import React, { useState, useEffect, useRef, useCallback } from "react";
import { FaSignOutAlt } from "react-icons/fa";
import { Spinner } from "react-bootstrap";

const LogoutModal = ({ show, onCancel, onConfirm, loading = false }) => {
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const dialogRef = useRef(null);
  const previousActiveRef = useRef(null);

  const handleKeyDown = useCallback(
    (e) => {
      if (!show) return;
      if (e.key === "Escape") {
        e.preventDefault();
        onCancel();
      }
    },
    [show, onCancel]
  );

  useEffect(() => {
    if (show) {
      const t = requestAnimationFrame(() => setIsVisible(true));
      return () => cancelAnimationFrame(t);
    }
    setIsVisible(false);
  }, [show]);

  useEffect(() => {
    if (!show) return;
    previousActiveRef.current = document.activeElement;
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      if (previousActiveRef.current && typeof previousActiveRef.current.focus === "function") {
        previousActiveRef.current.focus();
      }
    };
  }, [show, handleKeyDown]);

  useEffect(() => {
    if (show && dialogRef.current) {
      const focusable = dialogRef.current.querySelector(
        'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (focusable) focusable.focus();
    }
  }, [show]);

  if (!show) return null;

  const handleConfirm = async () => {
    setIsLoggingOut(true);
    try {
      await onConfirm();
    } finally {
      setIsLoggingOut(false);
    }
  };

  const isLoading = loading || isLoggingOut;

  return (
    <div
      className="modal-backdrop-accessible"
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: isVisible ? "rgba(15, 23, 42, 0.6)" : "rgba(15, 23, 42, 0)",
        backdropFilter: "blur(8px)",
        WebkitBackdropFilter: "blur(8px)",
        zIndex: 1055,
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        transition: "background 0.2s ease-out",
      }}
      onClick={(e) => e.target === e.currentTarget && !isLoading && onCancel()}
      onKeyDown={handleKeyDown}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="logout-modal-title"
        aria-describedby="logout-modal-desc"
        style={{
          width: "100%",
          maxWidth: "400px",
          margin: "1.5rem",
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? "scale(1)" : "scale(0.96)",
          transition: "opacity 0.2s ease-out, transform 0.2s ease-out",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="modal-content border-0"
          style={{
            borderRadius: "16px",
            overflow: "hidden",
            background: "var(--color-card-bg, #ffffff)",
            boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(0, 0, 0, 0.05)",
          }}
        >
          {/* Body-first layout: message is primary */}
          <div className="modal-body text-center px-4 pt-5 pb-4">
            <div
              className="rounded-circle mx-auto d-flex align-items-center justify-content-center mb-4"
              style={{
                width: "80px",
                height: "80px",
                background: "linear-gradient(145deg, #fef2f2 0%, #fee2e2 100%)",
                border: "2px solid rgba(239, 68, 68, 0.2)",
                boxShadow: "0 4px 14px rgba(239, 68, 68, 0.15)",
              }}
            >
              <FaSignOutAlt style={{ color: "#dc2626", fontSize: "2rem" }} />
            </div>
            <h5
              id="logout-modal-title"
              className="fw-bold mb-2"
              style={{
                color: "var(--color-text, #1e293b)",
                fontSize: "1.25rem",
                letterSpacing: "-0.02em",
              }}
            >
              Leave your account?
            </h5>
            <p
              id="logout-modal-desc"
              className="text-muted mb-0 mx-auto"
              style={{
                fontSize: "0.9375rem",
                lineHeight: 1.5,
                maxWidth: "320px",
              }}
            >
              You’ll need to sign in again to access your dashboard and content.
            </p>
            {isLoading && (
              <div className="mt-4 d-flex align-items-center justify-content-center gap-2 text-muted" style={{ fontSize: "0.875rem" }}>
                <Spinner animation="border" size="sm" variant="danger" />
                <span>Signing out...</span>
              </div>
            )}
          </div>

          <div
            className="modal-footer border-0 d-flex gap-3 px-4 pb-4 pt-0"
            style={{
              justifyContent: "center",
              flexWrap: "wrap",
              backgroundColor: "transparent",
            }}
          >
            <button
              type="button"
              className="btn btn-light fw-semibold px-4 py-2"
              style={{
                borderRadius: "10px",
                border: "1px solid var(--color-border, #e2e8f0)",
                color: "var(--color-text-secondary, #64748b)",
                fontSize: "0.9375rem",
                minWidth: "100px",
              }}
              onClick={onCancel}
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn fw-semibold px-4 py-2 d-flex align-items-center justify-content-center gap-2"
              style={{
                borderRadius: "10px",
                background: isLoading ? "#cbd5e1" : "linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)",
                border: "none",
                color: "#fff",
                fontSize: "0.9375rem",
                minWidth: "120px",
                boxShadow: isLoading ? "none" : "0 4px 14px rgba(220, 38, 38, 0.35)",
              }}
              onClick={handleConfirm}
              disabled={isLoading}
              aria-busy={isLoading}
            >
              {isLoading ? (
                <>
                  <Spinner animation="border" size="sm" style={{ borderColor: "rgba(255,255,255,0.5)", borderTopColor: "#fff" }} />
                  Signing out...
                </>
              ) : (
                <>
                  <FaSignOutAlt style={{ fontSize: "0.875rem" }} />
                  Yes, logout
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LogoutModal;
